/**
 * Cliente HTTP centralizado (fetch) del Admin Web.
 *
 * Responsabilidades:
 * - Inyectar `Authorization: Bearer <access token>` (token en memoria, nunca en
 *   `localStorage`).
 * - Enviar/recibir la cookie httpOnly de refresh (`credentials: 'include'`).
 * - Reintentar **una vez** ante `401` refrescando el access token, y notificar el
 *   cierre de sesión si el refresh falla.
 * - Normalizar los errores del backend (contrato ADR 0021) a `ApiError`.
 */
import { API_BASE_URL } from '@/lib/env';

/** Cuerpo de error uniforme del backend (ADR 0021). */
export interface ApiErrorBody {
  statusCode?: number;
  error?: string;
  message?: string | string[];
  path?: string;
  timestamp?: string;
  correlationId?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly correlationId?: string;
  readonly details?: unknown;

  constructor(params: { status: number; code: string; message: string; correlationId?: string; details?: unknown }) {
    super(params.message);
    this.name = 'ApiError';
    this.status = params.status;
    this.code = params.code;
    this.correlationId = params.correlationId;
    this.details = params.details;
  }
}

type AccessTokenProvider = () => string | null;
type RefreshHandler = () => Promise<string | null>;
type UnauthorizedHandler = () => void;

let getAccessToken: AccessTokenProvider = () => null;
let refreshAccessToken: RefreshHandler | null = null;
let onUnauthorized: UnauthorizedHandler | null = null;

/** Conecta el cliente con el proveedor de sesión (AuthContext). */
export function configureApiClient(config: {
  getAccessToken?: AccessTokenProvider;
  refresh?: RefreshHandler | null;
  onUnauthorized?: UnauthorizedHandler | null;
}): void {
  if (config.getAccessToken) {
    getAccessToken = config.getAccessToken;
  }
  if (config.refresh !== undefined) {
    refreshAccessToken = config.refresh;
  }
  if (config.onUnauthorized !== undefined) {
    onUnauthorized = config.onUnauthorized;
  }
}

/**
 * Snapshot del access token en memoria. Necesario para APIs que no permiten
 * headers (p. ej. `EventSource` para SSE). No persistir ni loguear este valor.
 */
export function getAccessTokenSnapshot(): string | null {
  return getAccessToken();
}

/**
 * Ejecuta el refresh de sesión configurado (si lo hay) y devuelve el nuevo access
 * token. Lo usa el cliente SSE para reintentar ante un `401` sin tocar el estado
 * de React. Devuelve `null` si no hay handler o el refresh falla.
 */
export async function requestSessionRefresh(): Promise<string | null> {
  if (!refreshAccessToken) {
    return null;
  }
  try {
    return await refreshAccessToken();
  } catch {
    return null;
  }
}

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Adjunta `Authorization: Bearer` (default `true`). */
  auth?: boolean;
  /** Intenta refrescar el access token ante un `401` (default `true`). */
  retryOnUnauthorized?: boolean;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

interface SendOptions {
  method: NonNullable<ApiRequestOptions['method']>;
  auth: boolean;
  retryOnUnauthorized: boolean;
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

/** Rutas de auth donde un 401 es respuesta legítima (no dispara refresh). */
const NO_REFRESH_PATHS = new Set([
  '/api/v1/admin/auth/login',
  '/api/v1/admin/auth/2fa/verify',
  '/api/v1/admin/auth/refresh',
  '/api/v1/admin/auth/enroll/setup',
  '/api/v1/admin/auth/enroll/complete',
]);

export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const sendOptions: SendOptions = {
    method: options.method ?? 'GET',
    auth: options.auth ?? true,
    retryOnUnauthorized: options.retryOnUnauthorized ?? true,
    body: options.body,
    signal: options.signal,
    headers: options.headers,
  };
  return send<T>(path, sendOptions, false);
}

async function send<T>(path: string, options: SendOptions, isRetry: boolean): Promise<T> {
  const headers = new Headers({ Accept: 'application/json', ...options.headers });
  // `FormData` (subida multipart, p. ej. medios DOOH) viaja tal cual: el navegador
  // fija `Content-Type` con el boundary, por lo que NO se serializa a JSON. El resto
  // de cuerpos usa JSON.
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  if (options.body !== undefined && !isFormData) {
    headers.set('Content-Type', 'application/json');
  }
  const token = options.auth ? getAccessToken() : null;
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: options.method,
      headers,
      credentials: 'include',
      body:
        options.body === undefined
          ? undefined
          : isFormData
            ? (options.body as FormData)
            : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch (error) {
    throw new ApiError({
      status: 0,
      code: 'NETWORK_ERROR',
      message: 'No se pudo contactar el servidor. Verifica tu conexión.',
      details: error,
    });
  }

  // 401 => refrescar una sola vez y reintentar la petición original.
  if (
    response.status === 401 &&
    !isRetry &&
    options.retryOnUnauthorized &&
    refreshAccessToken &&
    !NO_REFRESH_PATHS.has(path)
  ) {
    const renewedToken = await refreshAccessToken().catch(() => null);
    if (renewedToken) {
      return send<T>(path, { ...options, retryOnUnauthorized: false }, true);
    }
    onUnauthorized?.();
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const payload = await readBody(response);
  if (!response.ok) {
    const errorBody = (payload ?? {}) as ApiErrorBody;
    const rawMessage = errorBody.message;
    const message = Array.isArray(rawMessage)
      ? rawMessage.join(', ')
      : (rawMessage ?? `Error ${response.status}`);
    throw new ApiError({
      status: response.status,
      code: errorBody.error ?? 'HTTP_ERROR',
      message,
      correlationId: errorBody.correlationId,
      details: payload,
    });
  }

  return payload as T;
}

async function readBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') ?? '';
  const text = await response.text();
  if (!text) {
    return null;
  }
  if (!contentType.includes('application/json')) {
    return text;
  }
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}
